import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";

// Mapping option keys to href + color (kept here since URLs are fixed).
const OPTION_META = {
  "eigen-paard": { href: "/eigen-paard", color: "var(--forest)" },
  "manege-ruiter": { href: "/manege-ruiter", color: "var(--sand)" },
  trainer: { href: "/trainer", color: "var(--sand-dark)" },
  professional: { href: "/professional", color: "var(--forest)" },
  "manege-module": { href: "/manege-module", color: "var(--ink)" },
  fokkerij: { href: "/fokkerij", color: "var(--sand-dark)" },
};

export const ProfileSelector = () => {
  const { t } = useTranslation();
  const options = t("profileSelector.options", { returnObjects: true }) || [];

  return (
    <section
      id="profile"
      data-testid="profile-selector"
      className="bg-background py-20 lg:py-28"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-xl">
            <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
              {t("profileSelector.eyebrow")}
            </p>
            <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] text-[hsl(var(--ink))] text-balance sm:text-5xl">
              {t("profileSelector.title")}
            </h2>
          </div>
          <p className="max-w-md text-sm text-[hsl(var(--ink-soft))]">
            {t("profileSelector.intro")}
          </p>
        </div>

        <ul className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {options.map((p) => {
            const meta = OPTION_META[p.key] || { href: "/", color: "var(--forest)" };
            return (
              <li key={p.key}>
                <Link
                  to={meta.href}
                  data-testid={`profile-${p.key}`}
                  className="group flex items-center justify-between gap-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6 transition-all hover:-translate-y-0.5 hover:border-[hsl(var(--ink))]"
                >
                  <span className="flex items-center gap-4">
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: `hsl(${meta.color})` }}
                    />
                    <span className="font-display text-xl text-[hsl(var(--ink))]">
                      {p.label}
                    </span>
                  </span>
                  <ArrowRight
                    className="h-4 w-4 text-[hsl(var(--ink-soft))] transition-all group-hover:translate-x-1 group-hover:text-[hsl(var(--ink))]"
                    strokeWidth={1.6}
                  />
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export const Promises = () => {
  const { t } = useTranslation();
  const items = t("promises.items", { returnObjects: true }) || [];

  return (
    <section
      data-testid="promises"
      className="bg-[hsl(var(--surface-secondary))] py-20 lg:py-28"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
            {t("promises.eyebrow")}
          </p>
          <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] text-[hsl(var(--ink))] text-balance sm:text-5xl">
            {t("promises.title")}
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-3">
          {items.map((p) => (
            <article
              key={p.label}
              className="rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-8"
            >
              <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
                {p.label}
              </p>
              <h3 className="mt-4 font-display text-2xl leading-snug text-[hsl(var(--ink))] sm:text-3xl">
                {p.title}
              </h3>
              <p className="mt-4 text-[hsl(var(--ink-soft))]">{p.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
